import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type RecipeSummary } from '../api/endpoints';
import { ApiException } from '../api/client';
import { Skeleton, ErrorState, EmptyState } from '../components/states';
import { RecipeCard } from '../components/RecipeCard';
import { AddToMenuModal } from '../components/AddToMenuModal';

type Tab = 'favorites' | 'mine';

export function MyRecipesScreen() {
  const [tab, setTab] = useState<Tab>('favorites');
  return (
    <div className="px-4 pt-8">
      <h1 className="mb-4 text-[26px] font-bold leading-tight text-ink">Рецепты</h1>
      <div className="mb-6 flex gap-1 rounded-2xl bg-black/5 p-1">
        {([['favorites', 'Избранное'], ['mine', 'Мои']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            aria-pressed={tab === key}
            className={`flex-1 rounded-xl py-2 text-sm font-semibold transition ${tab === key ? 'bg-white text-ink shadow-sm' : 'text-ink/55'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'favorites' ? <FavoritesView /> : <MineView />}
    </div>
  );
}

function FavoritesView() {
  const navigate = useNavigate();
  const [items, setItems] = useState<RecipeSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuFor, setMenuFor] = useState<RecipeSummary | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await endpoints.getFavorites());
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить избранное');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    // Намеренный fetch-in-effect (план без TanStack): разовый setState на маунте — приемлемо.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  async function remove(id: string) {
    setItems((cur) => (cur ? cur.filter((r) => r.id !== id) : cur)); // оптимистично
    try {
      await endpoints.removeFavorite(id);
    } catch {
      setToast('Не удалось убрать — обновите список');
      void load();
    }
  }

  if (loading)
    return (
      <div className="space-y-4" data-testid="fav-loading">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-44 w-full" />
        ))}
      </div>
    );
  if (error) return <ErrorState message={error} onRetry={() => void load()} />;
  if (items && items.length === 0)
    return <EmptyState title="Пока нет избранного" hint="Откройте рецепт и нажмите ♥ — он появится здесь." />;

  return (
    <div className="stagger space-y-4">
      {(items ?? []).map((r) => (
        <div key={r.id} className="space-y-2">
          <RecipeCard
            title={r.title ?? ''}
            imageUrl={r.imageUrl ?? null}
            cookTimeMin={r.cookTimeMin}
            servings={r.servings}
            onClick={() => navigate(`/recipe/${r.id}`)}
          />
          <div className="flex gap-2">
            <button
              onClick={() => setMenuFor(r)}
              className="flex-1 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white active:scale-[0.98]"
            >
              В меню
            </button>
            <button
              onClick={() => void remove(r.id!)}
              className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-ink/60 shadow-sm active:scale-[0.98]"
            >
              Убрать
            </button>
          </div>
        </div>
      ))}
      {toast && <p role="status" className="text-center text-sm text-ink/60">{toast}</p>}
      {menuFor && (
        <AddToMenuModal
          recipeId={menuFor.id!}
          recipeTitle={menuFor.title ?? undefined}
          onAdded={() => setToast('Добавлено в меню')}
          onClose={() => setMenuFor(null)}
        />
      )}
    </div>
  );
}

function MineView() {
  const navigate = useNavigate();
  const [items, setItems] = useState<RecipeSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await endpoints.getMyRecipes());
    } catch (e) {
      setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить рецепты');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  return (
    <>
      <button
        onClick={() => navigate('/import')}
        className="mb-6 w-full rounded-xl border border-primary-600/70 px-4 py-3 font-semibold text-primary-600 transition hover:bg-primary-50 active:scale-[0.99]"
      >
        + Добавить по ссылке
      </button>

      {loading && (
        <div className="space-y-4" data-testid="my-loading">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      )}

      {!loading && error && <ErrorState message={error} onRetry={() => void load()} />}

      {!loading && !error && items && items.length === 0 && (
        <EmptyState
          title="Пока нет своих рецептов"
          hint="Добавьте рецепт по ссылке — он появится здесь."
          action={
            <button
              onClick={() => navigate('/import')}
              className="rounded-xl bg-primary-600 px-4 py-2.5 font-semibold text-white shadow-card transition active:scale-[0.98]"
            >
              Добавить по ссылке
            </button>
          }
        />
      )}

      {!loading && !error && items && items.length > 0 && (
        <div className="stagger space-y-4">
          {items.map((r) => (
            <RecipeCard
              key={r.id}
              title={r.title ?? ''}
              imageUrl={r.imageUrl ?? null}
              cookTimeMin={r.cookTimeMin}
              servings={r.servings}
              onClick={() => navigate(`/recipe/${r.id}`)}
            />
          ))}
        </div>
      )}
    </>
  );
}
